import { createSlice } from '@reduxjs/toolkit'
import { v4 as uuid } from 'uuid'

export const CartMode = Object.freeze({
    ClientSide: 'client-side',
    ServerSide: 'server-side',
})

export const CartItemTypes = Object.freeze({
    Course: 'course',
    Class: 'class',
    Plan: 'plan',
})

const initialState = {
    mode: CartMode.ClientSide,
    plan: null,
    items: [],
    itemDetails: {},
    appliedLoyaltyPoints: 0,
}

const PLAN_KEY = `plan-cart-item`

const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        selectPlan(state, action) {
            const cartItemId = action.payload.cartItemId || uuid()
            const plan = {
                cartItemId,
                type: CartItemTypes.Plan,
                id: action.payload.id,
                detailId: PLAN_KEY,
            }
            state.plan = plan
            state.itemDetails[plan.detailId] = action.payload.itemDetail
        },
        deselectPlan(state) {
            if (!state.plan) return
            delete state.itemDetails[state.plan.detailId]
            state.plan = null
        },
        addItem(state, action) {
            const cartItemId = action.payload.cartItemId || uuid()
            state.items.push({
                cartItemId,
                type: action.payload.type,
                id: action.payload.id,
                free: !!action.payload.free,
                invites: action.payload.invites,
                numFree: action.payload.numFree ?? 0,
            })
            state.itemDetails[cartItemId] = action.payload.itemDetail
        },
        removeItem(state, action) {
            state.items = state.items.filter(
                item => item.cartItemId !== action.payload.cartItemId
            )
            delete state.itemDetails[action.payload.cartItemId]
        },
        removeAllItems(state) {
            state.items = []
            state.plan = null
            state.itemDetails = {}
            state.appliedLoyaltyPoints = 0
        },
        applyLoyaltyPoints(state, action) {
            state.appliedLoyaltyPoints = action.payload
        },
        getServerCart(state, action) {
            state.mode = CartMode.ServerSide
            state.appliedLoyaltyPoints = 0
            state.itemDetails = {}
            state.items = []
            state.plan = null
            const plan = action.payload.find(
                item => item.type === CartItemTypes.Plan
            )
            if (!!plan) {
                const { itemDetail, ...planInfo } = plan
                state.plan = {
                    ...planInfo,
                    detailId: PLAN_KEY,
                }
                state.itemDetails[state.plan.detailId] = itemDetail
            }
            const otherItems = !!state.plan
                ? action.payload.filter(
                      item => item.cartItemId !== state.plan.cartItemId
                  )
                : action.payload
            for (const item of otherItems) {
                const { itemDetail, ...itemInfo } = item
                state.items.push(itemInfo)
                state.itemDetails[item.cartItemId] = itemDetail
            }
        },
        resetCart() {
            return {
                ...initialState,
            }
        },
    },
    extraReducers: {
        'user/logout'() {
            return { ...initialState }
        },
    },
})

const {
    selectPlan,
    deselectPlan,
    addItem,
    removeItem,
    removeAllItems,
    applyLoyaltyPoints,
    getServerCart,
    resetCart,
} = cartSlice.actions

export const cartActions = {
    selectPlan,
    deselectPlan,
    addItem,
    removeItem,
    removeAllItems,
    applyLoyaltyPoints,
    getServerCart,
    resetCart,
}

export const cartSelectors = {
    mode: state => state.cart.mode,
    selectedPlan: state => state.cart.plan,
    cartItems: state => state.cart.items,
    cartItemDetails: state => state.cart.itemDetails,
    appliedLoyaltyPoints: state => state.cart.appliedLoyaltyPoints,
    totalAmount(state) {
        const items = cartSelectors.cartItems(state)
        const itemDetails = cartSelectors.cartItemDetails(state)
        const appliedLoyaltyPoints = cartSelectors.appliedLoyaltyPoints(state)
        const coursesAmounts = items
            .filter(item => !item.free && item.type === CartItemTypes.Course)
            .map(item => itemDetails[item.cartItemId])
            .map(itemDetail => itemDetail.price)
        const classesAmounts = items
            .filter(item => item.type === CartItemTypes.Class)
            .map(item => ({
                price: itemDetails[item.cartItemId].price,
                invites: item.invites,
                numFree: item.numFree,
            }))
            .map(item => {
                const numPayments = Math.max(
                    0,
                    item.invites.length - item.numFree
                )
                return numPayments * item.price
            })
        const amount = [...classesAmounts, ...coursesAmounts].reduce(
            (sum, val) => sum + val,
            0
        )
        return amount - appliedLoyaltyPoints
    },
}

export default cartSlice.reducer
